import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../../../../config/config';
import Section from './Section';
import SectionForm, { SectionFormValues } from './SectionForm';
import { LinkFormValues } from './LinkForm';
import { COLLECTION_NAME, LinkItem, LinkSection, newId } from './types';

const sectionRef = (id: string) => doc(db, COLLECTION_NAME, id);

const Links = () => {
  const [sections, setSections] = useState<LinkSection[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [addingSection, setAddingSection] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState<LinkSection | null>(null);
  // Undo callbacks run after later edits, so they read the latest list from here.
  const latest = useRef<LinkSection[]>([]);
  latest.current = sections ?? [];

  useEffect(() => {
    let cancelled = false;

    const fetchSections = async () => {
      try {
        const snapshot = await getDocs(query(collection(db, COLLECTION_NAME), orderBy('order')));
        const loaded = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as LinkSection);
        if (!cancelled) setSections(loaded);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          toast.error('Failed to load links');
          setSections([]);
        }
      }
    };

    fetchSections();
    return () => {
      cancelled = true;
    };
  }, []);

  // Optimistic update: show the change now, roll back if Firestore rejects it.
  const commit = async (next: LinkSection[], write: () => Promise<void>, errorMessage: string) => {
    const prev = latest.current;
    latest.current = next;
    setSections(next);
    try {
      await write();
      return true;
    } catch (err) {
      console.error(err);
      setSections(prev);
      toast.error(errorMessage);
      return false;
    }
  };

  const withLinks = (list: LinkSection[], sectionId: string, links: LinkItem[]) =>
    list.map((s) => (s.id === sectionId ? { ...s, links } : s));

  const findSection = (sectionId: string) => latest.current.find((s) => s.id === sectionId);

  const saveLinks = (sectionId: string, links: LinkItem[], errorMessage: string) =>
    commit(
      withLinks(latest.current, sectionId, links),
      () => updateDoc(sectionRef(sectionId), { links }),
      errorMessage
    );

  const handleAddLink = (sectionId: string, { label, to }: LinkFormValues) => {
    const section = findSection(sectionId);
    if (!section) return;
    saveLinks(sectionId, [...section.links, { id: newId(), label, to }], 'Failed to add link');
  };

  const handleUpdateLink = (sectionId: string, linkId: string, values: LinkFormValues) => {
    const source = findSection(sectionId);
    const link = source?.links.find((l) => l.id === linkId);
    if (!source || !link) return;
    const updated = { ...link, label: values.label, to: values.to };

    if (values.sectionId === sectionId) {
      saveLinks(
        sectionId,
        source.links.map((l) => (l.id === linkId ? updated : l)),
        'Failed to update link'
      );
      return;
    }

    // Moving: take it out of one section and add it to the end of the other in one batch.
    const target = findSection(values.sectionId);
    if (!target) return;
    const sourceLinks = source.links.filter((l) => l.id !== linkId);
    const targetLinks = [...target.links, updated];
    const next = withLinks(withLinks(latest.current, sectionId, sourceLinks), target.id, targetLinks);

    commit(
      next,
      async () => {
        const batch = writeBatch(db);
        batch.update(sectionRef(sectionId), { links: sourceLinks });
        batch.update(sectionRef(target.id), { links: targetLinks });
        await batch.commit();
        toast.success(`Moved ${updated.label} to ${target.title}`);
      },
      'Failed to move link'
    );
  };

  const handleDeleteLink = (sectionId: string, link: LinkItem) => {
    const section = findSection(sectionId);
    if (!section) return;
    const index = section.links.findIndex((l) => l.id === link.id);

    const undo = () => {
      const current = findSection(sectionId);
      if (!current || current.links.some((l) => l.id === link.id)) return;
      const links = [...current.links];
      links.splice(Math.min(index, links.length), 0, link);
      saveLinks(sectionId, links, 'Failed to restore link');
    };

    saveLinks(
      sectionId,
      section.links.filter((l) => l.id !== link.id),
      'Failed to delete link'
    ).then((saved) => {
      if (!saved) return;
      toast(
        ({ closeToast }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, width: '100%' }}>
            <span>Deleted {link.label}</span>
            <Button
              size="small"
              color="secondary"
              onClick={() => {
                undo();
                closeToast?.();
              }}
            >
              Undo
            </Button>
          </Box>
        ),
        { autoClose: 6000 }
      );
    });
  };

  const handleAddSection = ({ title, icon }: SectionFormValues) => {
    const ref = doc(collection(db, COLLECTION_NAME));
    const order = latest.current.reduce((max, s) => Math.max(max, s.order + 1), 0);
    const section: LinkSection = { id: ref.id, title, icon, order, links: [] };

    setAddingSection(false);
    setOpenId(ref.id);
    commit(
      [...latest.current, section],
      () => setDoc(ref, { title, icon, order, links: [] }),
      'Failed to add section'
    );
  };

  const handleUpdateSection = (sectionId: string, values: SectionFormValues) => {
    commit(
      latest.current.map((s) => (s.id === sectionId ? { ...s, ...values } : s)),
      () => updateDoc(sectionRef(sectionId), { ...values }),
      'Failed to update section'
    );
  };

  const deleteSection = (section: LinkSection) => {
    setSectionToDelete(null);
    commit(
      latest.current.filter((s) => s.id !== section.id),
      async () => {
        await deleteDoc(sectionRef(section.id));
        toast.success(`Deleted ${section.title}`);
      },
      'Failed to delete section'
    );
  };

  // Empty sections go straight away; ones with links ask first.
  const handleDeleteSection = (section: LinkSection) => {
    if (section.links.length === 0) deleteSection(section);
    else setSectionToDelete(section);
  };

  const toggleEditMode = () => {
    setEditMode((prev) => !prev);
    setAddingSection(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, mb: 0.5 }}>
        <Typography variant="h6" color="primary">
          Links
        </Typography>
        {sections &&
          (editMode ? (
            <Button size="small" color="secondary" startIcon={<CheckIcon />} onClick={toggleEditMode}>
              Done
            </Button>
          ) : (
            <Tooltip title="Edit links">
              <IconButton size="small" aria-label="Edit links" onClick={toggleEditMode}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ))}
      </Box>

      {!sections ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {sections.map((section) => (
            <Section
              key={section.id}
              section={section}
              sections={sections}
              isOpen={openId === section.id}
              editMode={editMode}
              onToggle={(id) => setOpenId((prev) => (prev === id ? null : id))}
              onAddLink={handleAddLink}
              onUpdateLink={handleUpdateLink}
              onDeleteLink={handleDeleteLink}
              onUpdateSection={handleUpdateSection}
              onDeleteSection={handleDeleteSection}
            />
          ))}

          {sections.length === 0 && !editMode && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 1 }}>
              No links yet. Use the pencil to add a section.
            </Typography>
          )}

          {editMode &&
            (addingSection ? (
              <Box sx={{ px: 1, pt: 1 }}>
                <SectionForm
                  initial={{ title: '', icon: 'link' }}
                  submitLabel="Add"
                  onCancel={() => setAddingSection(false)}
                  onSubmit={handleAddSection}
                />
              </Box>
            ) : (
              <Button
                size="small"
                color="secondary"
                startIcon={<AddIcon />}
                onClick={() => setAddingSection(true)}
                sx={{ alignSelf: 'flex-start', mt: 1, ml: 1 }}
              >
                Add section
              </Button>
            ))}
        </Box>
      )}

      <Dialog open={!!sectionToDelete} onClose={() => setSectionToDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete section?</DialogTitle>
        <DialogContent dividers>
          <Typography>
            <strong style={{ textTransform: 'capitalize' }}>{sectionToDelete?.title}</strong> and its{' '}
            {sectionToDelete?.links.length} links will be deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSectionToDelete(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => sectionToDelete && deleteSection(sectionToDelete)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Links;
